import React, { useState, useMemo, useEffect } from "react";
import {
  Plus,
  HandshakeIcon,
  User as UserIcon,
  MapPin as MapPinIcon,
  CheckCircle as CheckCircleIcon,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  Euro as EuroIcon,
  ArrowUpDown as ArrowUpDownIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSponsorsFindQuery, useSponsorsCreationEntitiesQuery } from "@/lib/api/requests/sponsors.requests";
import { SponsorListResponseDto } from "@/lib/api/interfaces/sponsors.interface";
import { CreateSponsorDialog } from "@/page-components/sponsors/components/CreateSponsorDialog";
import {
  DataView,
  DataViewToolbar,
  DataViewLayout,
  DataViewContent,
  DataViewLoadingState,
  DataViewEmptyState,
  DataViewLoadMore,
  DataViewGridCard,
  DataViewListCard,
  GenericItem,
  GenericItemActions,
  EntityPreviewFrame,
} from "@/util-components/data-view";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import { EditSponsorDialog } from "@/page-components/sponsors/components/EditSponsorDialog";
import {
  useSponsorsDelete,
  useSponsorsFindOneQuery,
  useSponsorsBulkDelete,
} from "@/lib/api/requests/sponsors.requests";
import {
  PageInfoBanner,
  PrimaryActionButton,
} from "@/util-components/page-layout";
import { usePageState } from "@/lib/hooks/use-page-state";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { toast } from "@/lib/toast";
import { useAdvertisementsFindQuery } from "@/lib/api/requests/advertisements.requests";
import { AdvancedFilterDef, AdvancedState } from "@/util-components/data-view/components/advanced-filter-types";

/**
 * Anzahl der Sponsoren pro Seite
 */
const DEFAULT_PAGE_SIZE = 18;

/**
 * Sponsoren-Übersichtsseite (Liste/Grid mit Suche, Filtern & Aktionen)
 */
const Sponsors: React.FC = () => {
  const navigate = useNavigate();
  const { state, setInfoBannerVisible, setViewMode, setSettings } = usePageState<{
    advancedState: AdvancedState;
  }>("sponsors-list", {
    isInfoBannerVisible: true,
    viewMode: "grid",
    settings: {
      advancedState: { 
        filters: [], 
        sorts: [{ key: "name", direction: "asc" }] 
      },
    },
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [allSponsors, setAllSponsors] = useState<SponsorListResponseDto[]>([]);

  const viewMode = state.viewMode || "grid";

  // advancedState wird im PageState (LocalStorage) persistiert
  const advancedState = useMemo<AdvancedState>(
    () => {
      const s = (state.settings?.advancedState as AdvancedState) ?? { filters: [], sorts: [] };
      if (s.sorts.length === 0) {
        return { ...s, sorts: [{ key: 'name', direction: 'asc' }] };
      }
      return s;
    },
    [state.settings?.advancedState],
  );

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sponsorToDelete, setSponsorToDelete] =
    useState<SponsorListResponseDto | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [sponsorToEditId, setSponsorToEditId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Fetch full details for editing if an ID is selected
  const sponsorToEditQuery = useSponsorsFindOneQuery(sponsorToEditId || "", {
    enabled: !!sponsorToEditId && showEditDialog,
  });

  const deleteMutation = useSponsorsDelete({
    onSuccess: () => {
      sponsorsQuery.refetch();
    },
  });

  const bulkDeleteMutation = useSponsorsBulkDelete({
    onSuccess: () => {
      sponsorsQuery.refetch();
    },
  });

  // Leistungen (Advertisements) für den Filter laden (max. 20 pro Seite laut API)
  const advertisementsQuery = useAdvertisementsFindQuery({ limit: 20, page: 1 });
  const creationEntitiesQuery = useSponsorsCreationEntitiesQuery();

  // API-Filterparameter – Textsuche, Leistungsfilter und Paging
  const filterParams = useMemo(
    () => {
      const params: any = {
        textSearch: searchTerm.trim() || undefined,
        page: currentPage,
        limit: DEFAULT_PAGE_SIZE,
      };

      // Extract all filters from advancedState
      advancedState.filters.forEach((f) => {
        if (f.key === 'advertisementIds') params.advertisementIds = f.value;
        if (f.key === 'assignedUserId') params.assignedUserId = f.value;
        if (f.key === 'city') params.city = f.value;
        if (f.key === 'zip') params.zip = f.value;
        if (f.key === 'hasActiveAgreement') params.hasActiveAgreement = f.value === 'true' || f.value === true;
      });

      // Extract sorting from advancedState
      if (advancedState.sorts.length > 0) {
        params.sortBy = advancedState.sorts[0].key;
        params.sortOrder = advancedState.sorts[0].direction;
      }

      return params;
    },
    [searchTerm, currentPage, advancedState],
  );

  // API-Hooks
  const sponsorsQuery = useSponsorsFindQuery(filterParams);

  // Update sponsors when data changes
  useEffect(() => {
    if (sponsorsQuery.data?.docs) {
      if (currentPage === 1) {
        setAllSponsors(sponsorsQuery.data.docs);
      } else {
        setAllSponsors((prev) => [...prev, ...sponsorsQuery.data.docs]);
      }
    }
  }, [sponsorsQuery.data, currentPage]);

  // Check if there are more sponsors to load
  const hasMoreSponsors = sponsorsQuery.data?.hasNextPage || false;

  // Loading-States
  const isInitialLoading = sponsorsQuery.isLoading && currentPage === 1;
  const isLoadingMore = sponsorsQuery.isLoading && currentPage > 1;

  const handleSponsorClick = (sponsorId: string) => {
    navigate(`/sponsoring/sponsoren/${sponsorId}`);
  };

  const handleDelete = (id: string) => {
    const sponsor = allSponsors.find((s) => s._id === id);
    if (sponsor) {
      setSponsorToDelete(sponsor);
      setIsBulkDelete(false);
      setShowDeleteDialog(true);
    }
  };

  const handleSelectionDelete = () => {
    if (selectedIds.length > 0) {
      setSponsorToDelete(null);
      setIsBulkDelete(true);
      setShowDeleteDialog(true);
    }
  };

  const handleEdit = (id: string) => {
    setSponsorToEditId(id);
    setShowEditDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (isBulkDelete && selectedIds.length > 0) {
      await bulkDeleteMutation.mutateAsync({ ids: selectedIds });
      toast.success(`${selectedIds.length} Sponsoren erfolgreich gelöscht`);
      setAllSponsors((prev) =>
        prev.filter((s) => !selectedIds.includes(s._id)),
      );
      setCurrentPage(1);
      setSelectedIds([]);
      setShowDeleteDialog(false);
      setIsBulkDelete(false);
      return;
    }

    if (sponsorToDelete) {
      await deleteMutation.mutateAsync({ id: sponsorToDelete._id });
      toast.success("Sponsor erfolgreich gelöscht");
      setAllSponsors((prev) =>
        prev.filter((s) => s._id !== sponsorToDelete._id),
      );
      setCurrentPage(1);
      setShowDeleteDialog(false);
      setSponsorToDelete(null);
    }
  };

  const handleCreateSuccess = async (sponsorId: string) => {
    await sponsorsQuery.refetch();
    setCurrentPage(1);
    // Optional: direkt zur Detailansicht des neuen Sponsors navigieren
    // navigate(`/sponsors/sponsors/${sponsorId}`);
  };

  const handleLoadMore = () => {
    if (hasMoreSponsors && !isLoadingMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setAllSponsors([]);
  };

  const handleAdvancedStateChange = (newState: AdvancedState) => {
    setSettings(prev => ({ ...prev, advancedState: newState }));
    setCurrentPage(1);
    setAllSponsors([]);
  };

  const handleAdvancedReset = () => {
    setSettings(prev => ({ 
      ...prev, 
      advancedState: { 
        filters: [], 
        sorts: [{ key: "name", direction: "asc" }] 
      } 
    }));
    setSearchTerm("");
    setCurrentPage(1);
    setAllSponsors([]);
  };

  const handleViewModeChange = (newViewMode: "grid" | "list") => {
    setViewMode(newViewMode);
  };

  const convertToGenericItem = (
    sponsor: SponsorListResponseDto,
  ): GenericItem => {
    const logoUrl = sponsor.logoUrl;
    const abbreviation = sponsor.name.substring(0, 2).toUpperCase();

    return {
      id: sponsor._id,
      title: sponsor.name,
      previewContent: (
        <EntityPreviewFrame backgroundUrl={logoUrl}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={sponsor.name}
              className="max-h-full max-w-full object-contain"
              loading="lazy"
            />
          ) : (
            <span className="text-2xl font-bold tracking-tight text-blue-600 uppercase">
              {abbreviation}
            </span>
          )}
        </EntityPreviewFrame>
      ),
      previewContentList: (
        <div className="w-full h-full flex items-center justify-center overflow-hidden rounded-md bg-blue-50">
          {sponsor.logoUrl ? (
            <img
              src={sponsor.logoUrl}
              alt={sponsor.name}
              className="w-full h-full object-contain p-1.5"
              loading="lazy"
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-blue-600">
              <HandshakeIcon className="h-6 w-6" />
            </div>
          )}
        </div>
      ),
    };
  };

  const genericItems = useMemo(() => {
    return allSponsors.map(convertToGenericItem);
  }, [allSponsors]);

  const genericActions: GenericItemActions = {
    onItemClick: (id: string) => {
      handleSponsorClick(id);
    },
    onEdit: (id: string) => {
      handleEdit(id);
    },
    onDelete: (id: string) => {
      handleDelete(id);
    },
  };

  const filterDefs = useMemo<AdvancedFilterDef[]>(() => {
    const adOptions = (advertisementsQuery.data?.docs ?? []).map((ad) => ({
      value: ad._id,
      label: ad.name,
    }));

    const userOptions = (creationEntitiesQuery.data?.users ?? []).map((u) => ({
      value: u._id,
      label: u.value,
    }));

    return [
      {
        key: 'advertisementIds',
        label: 'Leistung',
        type: 'multi-select' as const,
        icon: HandshakeIcon,
        options: adOptions,
      },
      {
        key: 'assignedUserId',
        label: 'Betreuer',
        type: 'select' as const,
        icon: UserIcon,
        options: userOptions,
      },
      {
        key: 'city',
        label: 'Stadt',
        type: 'text' as const,
        icon: MapPinIcon,
      },
      {
        key: 'zip',
        label: 'PLZ',
        type: 'text' as const,
        icon: MapPinIcon,
      },
      {
        key: 'hasActiveAgreement',
        label: 'Aktiv-Status',
        type: 'select' as const,
        icon: CheckCircleIcon,
        options: [
          { value: 'true', label: 'Nur Aktive' },
          { value: 'false', label: 'Nur Inaktive' },
        ],
      },
    ];
  }, [advertisementsQuery.data, creationEntitiesQuery.data]);

  const sortDefs = useMemo(
    () => [
      { key: 'name', label: 'Name', icon: ArrowUpDownIcon },
      { key: 'volume', label: 'Volumen', icon: EuroIcon },
      { key: 'createdAt', label: 'Erstellt am', icon: CalendarIcon },
      { key: 'updatedAt', label: 'Geändert am', icon: ClockIcon },
    ],
    [],
  );

  return (
    <DataView
      viewMode={viewMode}
      onViewModeChange={handleViewModeChange}
      isLoading={isInitialLoading}
      isEmpty={allSponsors.length === 0}
      selectedIds={selectedIds}
      onMultiSelect={setSelectedIds}
      onSelectionDelete={handleSelectionDelete}
    >
      <div className="bg-white flex-shrink-0 border-b border-gray-100">
        {state.isInfoBannerVisible && (
          <div className="p-4 pb-0">
            <PageInfoBanner
              title="Sponsoren & Partner verwalten"
              description={
                <>
                  Hier finden Sie eine Übersicht all Ihrer Sponsoren und
                  Werbepartner. <br />
                  Erfassen Sie neue Partner, pflegen Sie Kontaktdaten und weisen
                  Sie vereinbarte Leistungen zu.
                </>
              }
              icon={HandshakeIcon}
              variant="blue"
              onClose={() => setInfoBannerVisible(false)}
            />
          </div>
        )}

        <DataViewToolbar
          searchQuery={searchTerm}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Sponsoren suchen..."
          infoText={`${sponsorsQuery.data?.count || 0} Sponsoren`}
          mode="advanced"
          advancedFilterDefs={filterDefs}
          advancedSortDefs={sortDefs}
          advancedState={advancedState}
          onAdvancedStateChange={handleAdvancedStateChange}
          onAdvancedReset={handleAdvancedReset}
          defaultSort={{ key: "name", direction: "asc" }}
          leading={
            <>
              <PermissionFeatureWrapper
                requiredPermission={Permission.MANAGE_ENTITIES}
                requiredPlan={PlanKey.BASIC}
              >
                <PrimaryActionButton
                  label="Sponsor hinzufügen"
                  icon={Plus}
                  onClick={() => setIsCreateDialogOpen(true)}
                />
              </PermissionFeatureWrapper>
            </>
          }
        />
      </div>

      <CreateSponsorDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSuccess={handleCreateSuccess}
      />

      <DataViewLayout>
        <DataViewContent
          items={genericItems}
          renderGridItem={(item) => (
            <DataViewGridCard item={item} actions={genericActions} />
          )}
          renderListItem={(item) => (
            <DataViewListCard item={item} actions={genericActions} />
          )}
          loadingIndicator={<DataViewLoadingState />}
          emptyState={
            <DataViewEmptyState
              icon={<HandshakeIcon className="h-8 w-8 text-gray-400" />}
              title={
                searchTerm.trim() !== ""
                  ? "Keine Sponsoren gefunden"
                  : "Noch keine Sponsoren vorhanden"
              }
              description={
                searchTerm.trim() !== ""
                  ? "Versuchen Sie andere Suchbegriffe."
                  : "Legen Sie Ihren ersten Sponsor an oder importieren Sie bestehende Sponsoren."
              }
              action={
                searchTerm.trim() === "" ? (
                  <PermissionFeatureWrapper
                    requiredPermission={Permission.MANAGE_ENTITIES}
                    requiredPlan={PlanKey.BASIC}
                  >
                    <PrimaryActionButton
                      label="Sponsor hinzufügen"
                      icon={Plus}
                      onClick={() => setIsCreateDialogOpen(true)}
                    />
                  </PermissionFeatureWrapper>
                ) : undefined
              }
            />
          }
        />

        <DataViewLoadMore
          onLoadMore={handleLoadMore}
          isLoadingMore={isLoadingMore}
          hasMore={hasMoreSponsors}
        />
      </DataViewLayout>

      {showDeleteDialog && (sponsorToDelete || isBulkDelete) && (
        <StandardDeleteDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          title={
            isBulkDelete
              ? `${selectedIds.length} Sponsoren löschen`
              : "Sponsor löschen"
          }
          description={
            isBulkDelete
              ? "Möchten Sie diese Sponsoren wirklich unwiderruflich löschen?"
              : "Möchten Sie diesen Sponsor wirklich löschen? Alle zugehörigen Daten werden entfernt."
          }
          itemTitle={sponsorToDelete?.name}
          itemsList={
            isBulkDelete
              ? selectedIds.map((id) => {
                  const s = allSponsors.find((s) => s._id === id);
                  return {
                    title: s ? s.name : id,
                  };
                })
              : undefined
          }
          onDelete={handleDeleteConfirm}
          isLoading={deleteMutation.isPending || bulkDeleteMutation.isPending}
        />
      )}

      {showEditDialog &&
        sponsorToEditId &&
        // We only render the dialog content when data is loaded to avoid flickering
        (sponsorToEditQuery.data ? (
          <EditSponsorDialog
            open={showEditDialog}
            onOpenChange={(open) => {
              setShowEditDialog(open);
              if (!open) setSponsorToEditId(null);
            }}
            sponsor={sponsorToEditQuery.data}
            onSuccess={() => {
              sponsorsQuery.refetch();
            }}
          />
        ) : null)}
    </DataView>
  );
};

export default Sponsors;
